import React, { useEffect, useRef, useState } from 'react'
import { Grid, FormSectionTitle, makeStyles } from '@paintscout/ui'
import type { MuiTheme as Theme, StyleClasses } from '@paintscout/ui'
import { useClientOptions, Editor, useUser } from '@paintscout/ui-business'
import { getObjectLabels } from '@paintscout/util/builder'
import { useQuote } from '../../../context'

const useStyles = makeStyles<Theme, NotesViewProps>(
  (theme) => ({
    root: {},
    editor: {
      '& #menu-bar': {
        top: 83,
        [theme.breakpoints.down('sm')]: {
          top: 68
        }
      }
    }
  }),
  { name: 'NotesView' }
)

export interface NotesViewProps {
  classes?: StyleClasses<typeof useStyles>
}

function NotesView(props: NotesViewProps) {
  const classes = useStyles(props)
  const { quote, isEditable, isNoteEditable, updateQuote } = useQuote()
  const { isTeam } = useUser()
  const { options } = useClientOptions()
  const estimatorNotes = quote?.estimatorNotes
  const objectLabels = getObjectLabels({ options })

  // Track the last content the editor wrote so we can distinguish internal
  // edits (user typing) from external updates (AI patch). Only external
  // changes should force a remount — internal changes must not or the editor
  // loses cursor position on every keystroke.
  const lastInternalContent = useRef(estimatorNotes)
  const [editorKey, setEditorKey] = useState(`notesView-editor-${isEditable}`)

  // Remount when edit mode toggles (original behaviour).
  useEffect(() => {
    setEditorKey(`notesView-editor-${isEditable}-${Date.now()}`)
  }, [isEditable])

  // Remount only when content changes from outside (AI patch), not from user typing.
  useEffect(() => {
    if (estimatorNotes !== lastInternalContent.current) {
      lastInternalContent.current = estimatorNotes
      setEditorKey(`notesView-editor-${isEditable}-${Date.now()}`)
    }
  }, [estimatorNotes])

  const handleChange = (value: any) => {
    if (quote.estimatorNotes === (value as string)) {
      return
    }
    lastInternalContent.current = value as string
    updateQuote({ quote: { ...quote, estimatorNotes: value as string } })
  }

  return (
    <Grid container spacing={0}>
      <Grid item xs={12}>
        <FormSectionTitle
          title={'Estimator Notes'}
          variant="h2"
          subTitle={`These notes are a "scratch pad" for estimators and won't appear anywhere else on the ${objectLabels.quote.value.toLowerCase()}`}
        />
      </Grid>
      <Grid item xs={12}>
        <Editor
          classes={{ root: classes.editor }}
          content={estimatorNotes}
          onChange={handleChange}
          fullWidth={true}
          disabled={(!isEditable && !isNoteEditable) || isTeam}
          /* this is nasty, but the slate editor was 0% interested
          in becoming not-disabled when changed to editable */
          key={editorKey}
          debounce
        />
      </Grid>
    </Grid>
  )
}

export default NotesView
